import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth, API } from "@/App";
import { upgradeImageUrl, FALLBACK_THUMB } from "@/lib/imageUtils";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
    Zap,
    LogIn,
    LogOut,
    LayoutDashboard,
    Search,
    RefreshCcw,
    ExternalLink,
    Newspaper,
} from "lucide-react";
import LiveScoresTicker from "@/components/LiveScoresTicker";

const NewsPage = () => {
    const { user, logout, isContentManager } = useAuth();
    const navigate = useNavigate();
    const [items, setItems] = useState([]);
    const [feeds, setFeeds] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedFeed, setSelectedFeed] = useState("all");
    const [search, setSearch] = useState("");
    const [refreshing, setRefreshing] = useState(false);

    const fetchNews = async (force = false) => {
        if (force) setRefreshing(true);
        try {
            const params = new URLSearchParams({ limit: 60 });
            if (selectedFeed !== "all") params.set("feed_id", selectedFeed);
            if (search) params.set("search", search);
            if (force) params.set("refresh", "true");
            const { data } = await axios.get(`${API}/news/external?${params.toString()}`);
            setItems(data?.items || []);
            setFeeds(data?.feeds || []);
        } catch (error) {
            console.error("Error fetching news:", error);
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    };

    useEffect(() => {
        fetchNews();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedFeed]);

    const handleSearch = (e) => {
        e.preventDefault();
        fetchNews();
    };

    const formatTime = (s) => {
        if (!s) return "";
        const d = new Date(s);
        if (isNaN(d.getTime())) return "";
        const diff = Math.floor((Date.now() - d.getTime()) / 60000);
        if (diff < 1) return "Sasa hivi";
        if (diff < 60) return `Dakika ${diff} zilizopita`;
        if (diff < 1440) return `Saa ${Math.floor(diff / 60)} zilizopita`;
        return d.toLocaleDateString("sw-TZ", { day: "numeric", month: "short" });
    };

    const sources = [...new Set(feeds.map((f) => f.source))];

    return (
        <div className="min-h-screen bg-obsidian" data-testid="news-page">
            {/* Top Nav */}
            <nav className="bg-electric">
                <div className="container-main">
                    <div className="flex items-center justify-between h-12">
                        <Link to="/" className="flex items-center gap-2">
                            <Zap className="h-6 w-6 text-white" />
                            <span className="dimbani-wordmark text-2xl text-white">
                                DIMBANI
                            </span>
                        </Link>
                        <div className="hidden md:flex items-center gap-3">
                            {user ? (
                                <>
                                    {isContentManager && (
                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            onClick={() => navigate("/admin")}
                                            className="text-white hover:bg-white/10"
                                        >
                                            <LayoutDashboard className="h-4 w-4 mr-1" /> Admin
                                        </Button>
                                    )}
                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={logout}
                                        className="text-white hover:bg-white/10"
                                    >
                                        <LogOut className="h-4 w-4" />
                                    </Button>
                                </>
                            ) : (
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => navigate("/login")}
                                    className="text-white hover:bg-white/10 font-bold"
                                >
                                    <LogIn className="h-4 w-4 mr-1" /> Ingia
                                </Button>
                            )}
                        </div>
                    </div>
                </div>
            </nav>

            <LiveScoresTicker />

            {/* Header */}
            <div className="bg-paper border-b border-border">
                <div className="container-main py-6">
                    <div className="flex items-center justify-between flex-wrap gap-4">
                        <div className="flex items-center gap-3">
                            <Newspaper className="h-8 w-8 text-electric" />
                            <div>
                                <h1 className="font-heading font-black text-3xl md:text-4xl text-white uppercase tracking-tight">
                                    Habari za Dunia
                                </h1>
                                <p className="text-white/60 text-sm mt-1">
                                    Habari za michezo kutoka BBC, Sky Sports, ESPN, CNN, Guardian, FIFA, CAF &amp; zaidi
                                </p>
                            </div>
                        </div>
                        <Button
                            variant="outline"
                            onClick={() => fetchNews(true)}
                            disabled={refreshing}
                            className="border-electric/30 text-electric hover:bg-electric/10"
                            data-testid="refresh-news-btn"
                        >
                            <RefreshCcw
                                className={`h-4 w-4 mr-1 ${refreshing ? "animate-spin" : ""}`}
                            />
                            Sasisha
                        </Button>
                    </div>
                </div>
            </div>

            {/* Filters */}
            <div className="bg-subtle border-b border-border">
                <div className="container-main py-3">
                    <div className="flex items-center gap-3 flex-wrap">
                        <form onSubmit={handleSearch} className="relative flex-1 min-w-[200px] max-w-md">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-white/40" />
                            <Input
                                placeholder="Tafuta habari..."
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                className="pl-9 bg-paper border-border text-white h-9"
                                data-testid="news-search-input"
                            />
                        </form>
                        <div className="flex items-center gap-1 overflow-x-auto scrollbar-hide">
                            <button
                                onClick={() => setSelectedFeed("all")}
                                className={`px-3 py-1.5 text-xs font-medium rounded whitespace-nowrap ${
                                    selectedFeed === "all"
                                        ? "bg-electric text-white"
                                        : "bg-paper text-white/60 hover:text-white"
                                }`}
                            >
                                Zote
                            </button>
                            {feeds.map((f) => (
                                <button
                                    key={f.id}
                                    onClick={() => setSelectedFeed(f.id)}
                                    className={`px-3 py-1.5 text-xs font-medium rounded whitespace-nowrap ${
                                        selectedFeed === f.id
                                            ? "bg-electric text-white"
                                            : "bg-paper text-white/60 hover:text-white"
                                    }`}
                                    data-testid={`feed-filter-${f.id}`}
                                >
                                    {f.name}
                                </button>
                            ))}
                        </div>
                    </div>
                    <div className="mt-2 text-white/40 text-[10px]">
                        Vyanzo: {sources.join(" · ")}
                    </div>
                </div>
            </div>

            {/* Articles grid */}
            <main className="container-main py-6">
                {loading ? (
                    <div className="text-center py-16 text-white/40">Inapakia habari...</div>
                ) : items.length === 0 ? (
                    <div className="text-center py-16">
                        <Newspaper className="h-12 w-12 text-white/20 mx-auto mb-3" />
                        <p className="text-white/60">Hakuna habari kwa sasa.</p>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {items.map((item, idx) => (
                            <NewsCard key={item.id} item={item} idx={idx} formatTime={formatTime} />
                        ))}
                    </div>
                )}
            </main>
        </div>
    );
};

const NewsCard = ({ item, idx, formatTime }) => {
    return (
        <a
            href={item.link}
            target="_blank"
            rel="noopener noreferrer"
            className="block group"
            data-testid={`news-card-${idx}`}
        >
            <Card className="bg-paper border-border overflow-hidden h-full hover:border-electric/50 transition-colors">
                <div className="aspect-[16/9] bg-subtle overflow-hidden">
                    <img
                        src={upgradeImageUrl(item.image, 1200) || FALLBACK_THUMB}
                        alt={item.title}
                        loading="lazy"
                        onError={(e) => {
                            e.currentTarget.src = FALLBACK_THUMB;
                        }}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                </div>
                <div className="p-4">
                    <div className="flex items-center justify-between mb-2">
                        <Badge
                            variant="outline"
                            className="border-electric/40 text-electric text-[10px] uppercase"
                        >
                            {item.source}
                        </Badge>
                        <span className="text-white/40 text-xs">{formatTime(item.published)}</span>
                    </div>
                    <h3 className="text-white font-heading font-bold text-lg leading-snug mb-2 line-clamp-3 group-hover:text-electric transition-colors">
                        {item.title}
                    </h3>
                    {item.summary && (
                        <p className="text-white/60 text-sm line-clamp-2">{item.summary}</p>
                    )}
                    <div className="mt-3 flex items-center gap-1 text-electric text-xs font-medium">
                        Soma Zaidi <ExternalLink className="h-3 w-3" />
                    </div>
                </div>
            </Card>
        </a>
    );
};

export default NewsPage;
